class MemberServiceHighlights extends HTMLElement {
  getHighlightsRoot() {
    if (this._highlightsRoot?.isConnected) {
      return this._highlightsRoot;
    }
    this._highlightsRoot = this.closest('.member-service-highlights');
    return this._highlightsRoot;
  }

  isMemberSlideActive(swiper) {
    const activeSlide = swiper?.slides?.[swiper.activeIndex];
    return Boolean(activeSlide?.classList.contains('slide-member'));
  }

  _setServiceHeight(heightPx) {
    const value = `${heightPx}px`;
    if (this._lastServiceHeight === value) {
      return;
    }

    this._lastServiceHeight = value;
    document.documentElement.style.setProperty('--member-banner-slide-service-height', value);
  }

  updateServiceHeight() {
    const highlightsRoot = this.getHighlightsRoot();
    if (!highlightsRoot) {
      return;
    }

    if (highlightsRoot.classList.contains('member-service-highlights--hidden')) {
      this._setServiceHeight(0);
      return;
    }

    this._setServiceHeight(Math.round(highlightsRoot.getBoundingClientRect().height));
  }

  scheduleUpdateServiceHeight() {
    if (this._serviceHeightRafId) {
      cancelAnimationFrame(this._serviceHeightRafId);
    }

    this._serviceHeightRafId = requestAnimationFrame(() => {
      this._serviceHeightRafId = null;
      this.updateServiceHeight();
    });
  }

  bindServiceHeightSync() {
    const highlightsRoot = this.getHighlightsRoot();
    if (!highlightsRoot || this._serviceHeightObserver) {
      return;
    }

    this._serviceHeightObserver = new ResizeObserver(() => {
      this.scheduleUpdateServiceHeight();
    });
    this._serviceHeightObserver.observe(highlightsRoot);
    this._serviceHeightObserver.observe(this);
    this.scheduleUpdateServiceHeight();
  }

  unbindServiceHeightSync() {
    if (this._serviceHeightRafId) {
      cancelAnimationFrame(this._serviceHeightRafId);
      this._serviceHeightRafId = null;
    }

    this._serviceHeightObserver?.disconnect();
    this._serviceHeightObserver = null;
    this._lastServiceHeight = null;
    document.documentElement.style.setProperty('--member-banner-slide-service-height', '0px');
  }

  updateHighlightsVisibilityFromSlideshow(swiper) {
    const highlightsRoot = this.getHighlightsRoot();
    if (!highlightsRoot || !swiper) return;

    const isMemberSlide = this.isMemberSlideActive(swiper);
    highlightsRoot.classList.toggle('member-service-highlights--hidden', !isMemberSlide);
    this.scheduleUpdateServiceHeight();
  }

  bindHomeSlideshowVisibility() {
    const tryBind = (retryCount = 0) => {
      const slideshowEl = document.querySelector('.home-slideshow-swiper');
      const slideshowSwiper = slideshowEl?.swiper;

      if (!slideshowSwiper) {
        if (retryCount < 50) {
          requestAnimationFrame(() => tryBind(retryCount + 1));
        }
        return;
      }

      if (this.homeSlideshowSwiper === slideshowSwiper) return;

      this.unbindHomeSlideshowVisibility();
      this.homeSlideshowSwiper = slideshowSwiper;
      this.onHomeSlideshowSlideChange = () => {
        this.updateHighlightsVisibilityFromSlideshow(slideshowSwiper);
      };

      slideshowSwiper.on('slideChangeTransitionEnd', this.onHomeSlideshowSlideChange);
      slideshowSwiper.on('slideChange', this.onHomeSlideshowSlideChange);
      this.updateHighlightsVisibilityFromSlideshow(slideshowSwiper);
    };

    tryBind();
  }

  unbindHomeSlideshowVisibility() {
    if (!this.homeSlideshowSwiper || !this.onHomeSlideshowSlideChange) return;

    this.homeSlideshowSwiper.off('slideChangeTransitionEnd', this.onHomeSlideshowSlideChange);
    this.homeSlideshowSwiper.off('slideChange', this.onHomeSlideshowSlideChange);
    this.homeSlideshowSwiper = null;
    this.onHomeSlideshowSlideChange = null;
  }

  connectedCallback() {
    this.bindHomeSlideshowVisibility();
    this.bindServiceHeightSync();

    if (this.swiperInstance || typeof Swiper === 'undefined') {
      return;
    }

    const tryInitSwiper = (retryCount = 0) => {
      if (this.swiperInstance) return;

      const containerWidth = this.clientWidth;
      if (containerWidth === 0) {
        if (retryCount < 20) {
          requestAnimationFrame(() => tryInitSwiper(retryCount + 1));
        }
        return;
      }

      this.swiperInstance = new Swiper(this, {
        spaceBetween: 20,
        followFinger: false,
        speed: 1000,
        loop: false,
        rewind: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        autoplay: {
          delay: 7000,
          stopOnLastSlide: false,
          disableOnInteraction: false,
        },
        breakpoints: {
          1001: { slidesPerView: 4, spaceBetween: 0 },
        },
      });

      this.swiperInstance.update();
      this.handleViewportChange();
      this.scheduleUpdateServiceHeight();
    };

    this.handleViewportChange = () => {
      if (!this.swiperInstance) return;

      const isMobileAutoPlay = window.matchMedia('(max-width: 1000px)').matches;
      if (isMobileAutoPlay) {
        this.swiperInstance.autoplay?.start();
      } else {
        this.swiperInstance.autoplay?.stop();
      }

      this.scheduleUpdateServiceHeight();
    };

    tryInitSwiper();
    window.addEventListener('resize', this.handleViewportChange);
  }

  disconnectedCallback() {
    this.unbindServiceHeightSync();
    this.unbindHomeSlideshowVisibility();
    this._highlightsRoot = null;

    if (this.handleViewportChange) {
      window.removeEventListener('resize', this.handleViewportChange);
      this.handleViewportChange = null;
    }

    if (this.swiperInstance && typeof this.swiperInstance.destroy === 'function') {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

if (!customElements.get('shokz-member-service-highlights')) {
  customElements.define('shokz-member-service-highlights', MemberServiceHighlights);
}
